// === Sidebar UI ===
// Session/project list rendering, sidebar open/close/swipe/resize, width helpers

import { send } from '../ws/connection';
import { showToast } from '../ui/toast';
import {
  normalizeAgent, invalidateSessionCache, getLastSessionForAgent, getAgentSessionStorageKey,
} from '../session/cache';
import { sessionState } from '../state';
import {
  SIDEBAR_SWIPE_TRIGGER, SIDEBAR_SWIPE_MAX_VERTICAL_DRIFT,
  SIDEBAR_DEFAULT_WIDTH, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH,
  SIDEBAR_WIDTH_STORAGE_KEY, DESKTOP_INSIGHTS_BREAKPOINT, SESSION_LIST_COLLATOR,
} from '../constants';
import { timeAgo } from '../utils';
import { logComposerFocusDebug } from './session-debug';
import { logProjectRemoveDebug } from './project-remove-debug';

// --- DI callbacks ---
let _getProjects: (() => any[]) | null = null;
let _getSelectedAgent: (() => string) | null = null;
let _getSavedModeForAgent: ((agent: string) => string) | null = null;
let _getSkipDeleteConfirm: (() => boolean) | null = null;
let _setPendingProjectSaveCallback: ((cb: ((projects: any[]) => void) | null) => void) | null = null;
let _openSession: ((id: string, opts?: any) => void) | null = null;
let _resetChatView: ((agent?: string) => void) | null = null;
let _renderWorkspaceInsights: (() => void) | null = null;
let _showDeleteConfirm: ((agent: string, cb: () => void) => void) | null = null;
let _getVisibleSessions: (() => any[]) | null = null;

export function setupSidebarHandlers(handlers: {
  getProjects: () => any[];
  getSelectedAgent: () => string;
  getSavedModeForAgent: (agent: string) => string;
  getSkipDeleteConfirm: () => boolean;
  setPendingProjectSaveCallback: (cb: ((projects: any[]) => void) | null) => void;
  openSession: (id: string, opts?: any) => void;
  resetChatView: (agent?: string) => void;
  renderWorkspaceInsights: () => void;
  showDeleteConfirm: (agent: string, cb: () => void) => void;
  getVisibleSessions: () => any[];
}): void {
  _getProjects = handlers.getProjects;
  _getSelectedAgent = handlers.getSelectedAgent;
  _getSavedModeForAgent = handlers.getSavedModeForAgent;
  _getSkipDeleteConfirm = handlers.getSkipDeleteConfirm;
  _setPendingProjectSaveCallback = handlers.setPendingProjectSaveCallback;
  _openSession = handlers.openSession;
  _resetChatView = handlers.resetChatView;
  _renderWorkspaceInsights = handlers.renderWorkspaceInsights;
  _showDeleteConfirm = handlers.showDeleteConfirm;
  _getVisibleSessions = handlers.getVisibleSessions;
}

// --- Module-level state ---
let sidebarSwipe: any = null;
let sidebarResizeState: any = null;
const collapsedProjects: Set<string> = new Set(parseStoredCollapsedProjects());
let nextProjectSessionOrder = 1;
const projectSessionOrder = new Map<string, number>();

// --- Lazy DOM helpers ---
function getSessionList(): HTMLElement | null { return document.getElementById('session-list'); }
function getSidebar(): HTMLElement | null { return document.getElementById('sidebar'); }
function getSidebarOverlay(): HTMLElement | null { return document.getElementById('sidebar-overlay'); }
function getSidebarResizer(): HTMLElement | null { return document.getElementById('sidebar-resizer'); }

// ============================================================
// Sidebar width helpers
// ============================================================

export function getSidebarWidthLimit(): number {
  const reserveWidth = window.matchMedia(`(min-width: ${DESKTOP_INSIGHTS_BREAKPOINT}px)`).matches ? 860 : 440;
  const viewportMax = Math.max(SIDEBAR_MIN_WIDTH, window.innerWidth - reserveWidth);
  return Math.min(SIDEBAR_MAX_WIDTH, viewportMax);
}

export function clampSidebarWidth(width: number): number {
  const numericWidth = Number(width);
  if (!Number.isFinite(numericWidth)) return SIDEBAR_DEFAULT_WIDTH;
  return Math.max(SIDEBAR_MIN_WIDTH, Math.min(getSidebarWidthLimit(), Math.round(numericWidth)));
}

export function applySidebarWidth(width: number, options: any = {}): number {
  const nextWidth = clampSidebarWidth(width);
  document.documentElement.style.setProperty('--sidebar-width', `${nextWidth}px`);
  if (!options.skipPersist) {
    localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(nextWidth));
  }
  return nextWidth;
}

export function canResizeSidebar(): boolean {
  return !!getSidebarResizer() && window.matchMedia('(min-width: 769px) and (pointer: fine)').matches;
}

export function syncSidebarWidthForViewport(): void {
  if (!canResizeSidebar()) {
    document.body.classList.remove('sidebar-resizing');
    sidebarResizeState = null;
    return;
  }
  const savedWidth = parseInt(localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY) || `${SIDEBAR_DEFAULT_WIDTH}`, 10);
  applySidebarWidth(savedWidth, { skipPersist: true });
}

// ============================================================
// Dismissed virtual projects persistence
// Paths the user has explicitly removed — suppresses re-emergence as virtual groups
// ============================================================

const DISMISSED_VIRTUAL_PROJECTS_KEY = 'webcoding-dismissed-virtual-projects';

function normalizeDismissedVirtualProjectPath(pathValue: any): string {
  return normalizeComparablePath(pathValue);
}

function parseDismissedVirtualProjects(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_VIRTUAL_PROJECTS_KEY) || '[]';
    const parsed = JSON.parse(raw);
    return new Set((Array.isArray(parsed) ? parsed : [])
      .map((pathValue) => normalizeDismissedVirtualProjectPath(pathValue))
      .filter(Boolean));
  } catch {
    return new Set();
  }
}

const dismissedVirtualProjects: Set<string> = parseDismissedVirtualProjects();

function saveDismissedVirtualProjects(): void {
  localStorage.setItem(DISMISSED_VIRTUAL_PROJECTS_KEY, JSON.stringify([...dismissedVirtualProjects]));
}

function dismissVirtualProject(path: string): void {
  const normalizedPath = normalizeDismissedVirtualProjectPath(path);
  if (!normalizedPath) return;
  dismissedVirtualProjects.add(normalizedPath);
  saveDismissedVirtualProjects();
  logProjectRemoveDebug('dismiss_virtual_project', {
    rawPath: path,
    normalizedPath,
    dismissed: [...dismissedVirtualProjects],
  });
}

export function undismissVirtualProject(path: string): void {
  const normalizedPath = normalizeDismissedVirtualProjectPath(path);
  if (!normalizedPath) return;
  dismissedVirtualProjects.delete(normalizedPath);
  saveDismissedVirtualProjects();
  logProjectRemoveDebug('undismiss_virtual_project', {
    rawPath: path,
    normalizedPath,
    dismissed: [...dismissedVirtualProjects],
  });
}

// ============================================================
// Collapsed projects persistence
// ============================================================

function parseStoredCollapsedProjects(): string[] {
  try {
    const raw = localStorage.getItem('webcoding-collapsed-projects') || '[]';
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveCollapsedProjects(): void {
  localStorage.setItem('webcoding-collapsed-projects', JSON.stringify([...collapsedProjects]));
}

function rememberProjectSessionOrder(sessions: any[]): void {
  const seenIds = new Set<string>();
  for (const session of Array.isArray(sessions) ? sessions : []) {
    const sessionId = String(session?.id || '').trim();
    if (!sessionId) continue;
    seenIds.add(sessionId);
    if (!projectSessionOrder.has(sessionId)) {
      projectSessionOrder.set(sessionId, nextProjectSessionOrder++);
    }
  }
  for (const sessionId of Array.from(projectSessionOrder.keys())) {
    if (!seenIds.has(sessionId)) projectSessionOrder.delete(sessionId);
  }
}

// ============================================================
// Session/project list utilities
// ============================================================

export function getSessionUpdatedTimestamp(session: any): number {
  const next = new Date(session?.updated || 0).getTime();
  return Number.isFinite(next) ? next : 0;
}

export function getLatestSessionTimestamp(groupSessions: any[], timestampCache: Map<string, number> | null = null): number {
  return (Array.isArray(groupSessions) ? groupSessions : []).reduce((latest: number, session: any) => {
    const next = timestampCache?.get(session?.id) ?? getSessionUpdatedTimestamp(session);
    return next > latest ? next : latest;
  }, 0);
}

function getProjectSortPriority(project: any): number {
  const priority = Number(project?.sortPriority || 0);
  return Number.isFinite(priority) ? priority : 0;
}

export function normalizeComparablePath(pathValue: any): string {
  if (!pathValue) return '';
  const normalized = String(pathValue).trim().replace(/\\/g, '/').replace(/\/+$/, '');
  return normalized || '/';
}

export function getPathLeaf(pathValue: any): string {
  const normalized = normalizeComparablePath(pathValue);
  if (!normalized || normalized === '/') return pathValue || '/';
  const parts = normalized.split('/');
  return parts[parts.length - 1] || normalized;
}

export function decodeClaudeProjectDir(projectDir: any): string | null {
  const raw = String(projectDir || '').trim();
  if (!raw) return null;
  if (raw.startsWith('-') && !raw.includes('/') && !raw.includes('\\')) {
    const parts = raw.split('-').filter(Boolean);
    if (parts.length > 0) return `/${parts.join('/')}`;
  }
  return raw;
}

export function formatClaudeImportGroupPath(projectDir: any): string {
  const raw = String(projectDir || '').trim();
  if (!raw) return '/';
  const decoded = decodeClaudeProjectDir(raw);
  if (decoded && decoded !== raw) {
    return normalizeComparablePath(decoded);
  }
  let readablePath = raw.replace(/-/g, '/');
  if (!readablePath.startsWith('/')) readablePath = '/' + readablePath;
  return readablePath.replace(/\/+/g, '/');
}

export function isSameOrChildPath(parentPath: any, childPath: any): boolean {
  const parent = normalizeComparablePath(parentPath);
  const child = normalizeComparablePath(childPath);
  if (!parent || !child) return false;
  if (parent === child) return true;
  if (parent === '/') return child.startsWith('/');
  return child.startsWith(`${parent}/`);
}

export function findBestProjectForSession(session: any, projectsById: Map<string, any>): any {
  const projects = _getProjects?.() || [];
  const comparablePath = session.cwd || decodeClaudeProjectDir(session.importedFrom);
  if (session.projectId && projectsById.has(session.projectId)) {
    return projectsById.get(session.projectId);
  }
  if (!comparablePath) return null;
  let matchedProject = null;
  let matchedPathLength = -1;
  for (const project of projects) {
    if (!project?.path || !isSameOrChildPath(project.path, comparablePath)) continue;
    const projectPathLength = normalizeComparablePath(project.path).length;
    if (projectPathLength > matchedPathLength) {
      matchedProject = project;
      matchedPathLength = projectPathLength;
    }
  }
  return matchedProject;
}

function buildVirtualProjectFromCwd(cwd: string): any {
  const normalizedCwd = normalizeComparablePath(cwd);
  return {
    id: `__cwd__:${normalizedCwd || cwd}`,
    name: getPathLeaf(normalizedCwd || cwd) || '当前目录',
    path: cwd,
    isVirtualCwd: true,
  };
}

export function buildVirtualProjectFromSession(session: any): any {
  const derivedPath = session.cwd || decodeClaudeProjectDir(session.importedFrom);
  if (!derivedPath) return null;
  return buildVirtualProjectFromCwd(derivedPath);
}

function getProjectParentPath(project: any): string {
  const p = project.path;
  if (!p) return '';
  const idx = p.lastIndexOf('/');
  if (idx <= 0) return p;
  return p.substring(0, idx);
}

function findProjectGroupElement(projectId: string): Element | null {
  const sessionList = getSessionList();
  if (!sessionList) return null;
  return [...sessionList.querySelectorAll('.project-group')].find((node) => (node as HTMLElement).dataset.projectId === projectId) || null;
}

export function focusProjectGroup(projectId: string, options: any = {}): boolean {
  if (!projectId) return false;

  if (collapsedProjects.has(projectId)) {
    collapsedProjects.delete(projectId);
    saveCollapsedProjects();
    renderSessionList();
  }

  const target = findProjectGroupElement(projectId);
  if (!target) return false;

  requestAnimationFrame(() => {
    target.scrollIntoView({ block: 'nearest', behavior: options.instant ? 'auto' : 'smooth' });
    target.classList.remove('project-group-focused');
    void (target as HTMLElement).offsetWidth;
    target.classList.add('project-group-focused');
    window.setTimeout(() => target.classList.remove('project-group-focused'), 1400);
  });

  if (options.toast) showToast(options.toast);
  return true;
}

function showSidebarContextMenu(anchor: HTMLElement, items: any[]): void {
  document.querySelector('.sidebar-ctx-menu')?.remove();
  const menu = document.createElement('div');
  menu.className = 'sidebar-ctx-menu';
  for (const item of items) {
    const btn = document.createElement('button');
    btn.className = 'sidebar-ctx-menu-item' + (item.danger ? ' danger' : '');
    btn.type = 'button';
    btn.textContent = item.label;
    btn.addEventListener('click', (e) => { e.stopPropagation(); menu.remove(); item.action(); });
    menu.appendChild(btn);
  }
  document.body.appendChild(menu);
  const rect = anchor.getBoundingClientRect();
  const mw = menu.offsetWidth || 160;
  let left = rect.right - mw;
  if (left < 8) left = rect.left;
  let top = rect.bottom + 4;
  if (top + menu.offsetHeight + 8 > window.innerHeight) top = rect.top - menu.offsetHeight - 4;
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
  const dismiss = (e: Event) => {
    if (!(menu.contains(e.target as Node))) {
      menu.remove();
      document.removeEventListener('click', dismiss, { capture: true });
    }
  };
  setTimeout(() => document.addEventListener('click', dismiss, { capture: true }), 0);
}

function getLoadedMessageCountForSession(sessionId: string): number | null {
  if (sessionId === sessionState.currentSessionId) {
    return Number.isFinite(sessionState.currentHistoryBuffered) ? sessionState.currentHistoryBuffered : null;
  }

  const cachedEntry = sessionState.sessionCache.get(sessionId) as { snapshot?: { historyBuffered?: number; messages?: unknown[] } } | undefined;
  const cachedSnapshot = cachedEntry?.snapshot;
  if (!cachedSnapshot) return null;
  if (Number.isFinite(cachedSnapshot.historyBuffered)) return cachedSnapshot.historyBuffered as number;
  return Array.isArray(cachedSnapshot.messages) ? cachedSnapshot.messages.length : null;
}

export function updateSessionItemLoadedMessageCount(sessionId: string): void {
  const count = getLoadedMessageCountForSession(sessionId);
  const item = Array.from(document.querySelectorAll<HTMLElement>('.session-item'))
    .find((element) => element.dataset.id === sessionId);
  const countEl = item?.querySelector<HTMLElement>('.session-item-updated-count');
  if (!countEl) return;
  countEl.textContent = count === null ? '—' : `${count}`;
}

function buildSessionItem(s: any): HTMLElement {
  const showUnread = !!(s.hasUnread && s.id !== sessionState.currentSessionId);
  const loadedMessageCount = getLoadedMessageCountForSession(s.id);
  const item = document.createElement('div');
  item.className = `session-item${s.id === sessionState.currentSessionId ? ' active' : ''}${showUnread ? ' has-unread' : ''}${s.isRunning ? ' is-running' : ''}`;
  item.dataset.id = s.id;
  item.setAttribute('tabindex', '0');
  item.setAttribute('role', 'button');
  item.setAttribute('aria-label', `会话: ${s.title || 'Untitled'}`);

  const leading = document.createElement('span');
  leading.className = 'session-item-leading';
  leading.setAttribute('aria-hidden', 'true');

  const marker = document.createElement('span');
  marker.className = 'session-item-marker';
  leading.appendChild(marker);

  const content = document.createElement('div');
  content.className = 'session-item-content';

  const title = document.createElement('span');
  title.className = 'session-item-title';
  title.textContent = s.title || 'Untitled';
  title.title = s.title || 'Untitled';

  const right = document.createElement('div');
  right.className = 'session-item-right';

  const updatedTime = document.createElement('span');
  updatedTime.className = 'session-item-updated';
  const updatedTimeText = document.createElement('span');
  updatedTimeText.className = 'session-item-updated-time';
  updatedTimeText.textContent = timeAgo(s.updated, { compact: true }) || '';
  const messageCountText = document.createElement('span');
  messageCountText.className = 'session-item-updated-count';
  messageCountText.textContent = loadedMessageCount === null ? '—' : `${loadedMessageCount}`;
  updatedTime.title = s.updated ? new Date(s.updated).toLocaleString('en-US') : '';
  updatedTime.appendChild(updatedTimeText);
  updatedTime.appendChild(messageCountText);
  right.appendChild(updatedTime);

  const editBtn = document.createElement('button');
  editBtn.className = 'session-item-btn edit';
  editBtn.title = '重命名';
  editBtn.type = 'button';
  editBtn.setAttribute('aria-label', '重命名会话');
  editBtn.textContent = '✎';
  right.appendChild(editBtn);

  const deleteBtn = document.createElement('button');
  deleteBtn.className = 'session-item-btn delete';
  deleteBtn.title = '删除';
  deleteBtn.type = 'button';
  deleteBtn.setAttribute('aria-label', '删除会话');
  deleteBtn.textContent = '×';
  right.appendChild(deleteBtn);

  const moreBtn = document.createElement('button');
  moreBtn.className = 'session-item-btn session-item-more-btn';
  moreBtn.title = '更多操作';
  moreBtn.type = 'button';
  moreBtn.setAttribute('aria-label', '更多操作');
  moreBtn.textContent = '⋯';
  moreBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const doDelete = () => {
      const sessionAgent = normalizeAgent(s.agent);
      if (getLastSessionForAgent(sessionAgent) === s.id) localStorage.removeItem(getAgentSessionStorageKey(sessionAgent));
      if (localStorage.getItem('webcoding-session') === s.id) localStorage.removeItem('webcoding-session');
      invalidateSessionCache(s.id);
      send({ type: 'delete_session', sessionId: s.id });
      const selectedAgent = _getSelectedAgent?.() || 'claude';
      if (s.id === sessionState.currentSessionId) _resetChatView?.(selectedAgent);
    };
    showSidebarContextMenu(moreBtn, [
      { label: '✎ 重命名', action: () => startEditSessionTitle(item, s) },
      { label: '× 删除', danger: true, action: () => { if (_getSkipDeleteConfirm?.()) doDelete(); else _showDeleteConfirm?.(s.agent, doDelete); } },
    ]);
  });
  right.appendChild(moreBtn);

  content.appendChild(title);
  content.appendChild(right);

  item.appendChild(leading);
  item.appendChild(content);

  item.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const actionBtn = target?.closest('button') || null;
    const sidebarOpen = !!getSidebar()?.classList.contains('open');
    const clickPayload = {
      sessionId: s.id,
      currentSessionId: sessionState.currentSessionId || null,
      targetTag: target?.tagName || null,
      targetClass: target?.className || null,
      actionButtonClass: actionBtn?.className || null,
      defaultPrevented: e.defaultPrevented,
      sidebarOpen,
    };
    if (actionBtn?.classList.contains('delete')) {
      e.stopPropagation();
      const doDelete = () => {
        const sessionAgent = normalizeAgent(s.agent);
        if (getLastSessionForAgent(sessionAgent) === s.id) {
          localStorage.removeItem(getAgentSessionStorageKey(sessionAgent));
        }
        if (localStorage.getItem('webcoding-session') === s.id) localStorage.removeItem('webcoding-session');
        invalidateSessionCache(s.id);
        send({ type: 'delete_session', sessionId: s.id });
        const selectedAgent = _getSelectedAgent?.() || 'claude';
        if (s.id === sessionState.currentSessionId) {
          _resetChatView?.(selectedAgent);
        }
      };
      if (_getSkipDeleteConfirm?.()) {
        doDelete();
      } else {
        _showDeleteConfirm?.(s.agent, doDelete);
      }
      return;
    }
    if (actionBtn?.classList.contains('edit')) {
      e.stopPropagation();
      startEditSessionTitle(item, s);
      return;
    }
    logComposerFocusDebug('sidebar:click-open-session', {
      sessionId: s.id,
      currentSessionId: sessionState.currentSessionId || null,
      targetTag: target?.tagName || null,
    });
    _openSession?.(s.id, { focusComposer: true, closeSidebarOnApply: true });
  });

  item.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      logComposerFocusDebug('sidebar:keyboard-open-session', {
        sessionId: s.id,
        currentSessionId: sessionState.currentSessionId || null,
        key: e.key,
      });
      _openSession?.(s.id, { focusComposer: true, closeSidebarOnApply: true });
    }
  });

  return item;
}

function renderProjectGroup(project: any, groupSessions: any[], container: DocumentFragment | HTMLElement, timestampCache: Map<string, number>): void {
  const isVirtualCwd = Boolean(project.isVirtualCwd);
  const containsCurrentSession = groupSessions.some((session) => session.id === sessionState.currentSessionId);
  const isCollapsed = collapsedProjects.has(project.id);
  const runningCount = groupSessions.reduce((count, session) => count + (session.isRunning ? 1 : 0), 0);
  const unreadCount = groupSessions.reduce((count, session) => count + (session.hasUnread ? 1 : 0), 0);

  const group = document.createElement('section');
  group.className = 'project-group'
    + (containsCurrentSession ? ' active-project' : '')
    + (groupSessions.length === 0 ? ' empty-project' : '')
    + (runningCount ? ' has-running' : '')
    + (unreadCount ? ' has-unread' : '');
  group.dataset.projectId = project.id;
  group.dataset.sessionCount = String(groupSessions.length);
  if (runningCount) group.dataset.runningCount = String(runningCount);
  if (unreadCount) group.dataset.unreadCount = String(unreadCount);

  const header = document.createElement('div');
  header.className = 'project-group-header' + (isCollapsed ? ' collapsed' : '');
  header.dataset.projectId = project.id;
  header.setAttribute('aria-expanded', String(!isCollapsed));
  header.setAttribute('aria-label', `${project.name}，${groupSessions.length} 个会话`);

  const main = document.createElement('div');
  main.className = 'project-group-main';

  const chevron = document.createElement('span');
  chevron.className = 'project-group-chevron';
  chevron.textContent = '▸';

  const copy = document.createElement('div');
  copy.className = 'project-group-copy';

  const nameSpan = document.createElement('span');
  nameSpan.className = 'project-group-name';
  nameSpan.textContent = project.name;
  nameSpan.title = project.name;

  const pathLine = document.createElement('div');
  pathLine.className = 'project-group-path';
  const parentPath = getProjectParentPath(project);
  pathLine.textContent = parentPath;
  if (project.path) pathLine.title = project.path;

  const countBadge = document.createElement('span');
  countBadge.className = 'project-group-count';
  countBadge.textContent = String(groupSessions.length);
  countBadge.title = `${groupSessions.length} 个会话`;

  copy.appendChild(nameSpan);
  if (parentPath) copy.appendChild(pathLine);

  main.appendChild(chevron);
  main.appendChild(copy);
  main.appendChild(countBadge);

  const actions = document.createElement('div');
  actions.className = 'project-group-actions';
  const createBtn = document.createElement('button');
  createBtn.className = 'project-group-create-btn';
  createBtn.title = '在此项目下新建会话';
  createBtn.type = 'button';
  createBtn.textContent = '+';
  createBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const selectedAgent = _getSelectedAgent?.() || 'claude';
    const nextMode = _getSavedModeForAgent?.(selectedAgent) || 'yolo';
    send(isVirtualCwd
      ? { type: 'new_session', cwd: project.path, agent: selectedAgent, mode: nextMode }
      : { type: 'new_session', projectId: project.id, agent: selectedAgent, mode: nextMode });
  });
  actions.appendChild(createBtn);
  const renameBtn = document.createElement('button');
  renameBtn.className = 'project-group-btn';
  renameBtn.title = '重命名';
  renameBtn.type = 'button';
  renameBtn.setAttribute('aria-label', '重命名项目');
  renameBtn.textContent = '✎';
  renameBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (isVirtualCwd) {
      undismissVirtualProject(project.path);
      _setPendingProjectSaveCallback?.((updatedProjects) => {
        const saved = updatedProjects.find((p) => p.path === project.path);
        if (saved) {
          const groupEl = findProjectGroupElement(saved.id);
          const headerEl = groupEl?.querySelector('.project-group-header');
          if (headerEl) startEditProjectName(headerEl as HTMLElement, saved);
        }
      });
      send({ type: 'save_project', path: project.path, name: project.name });
    } else {
      startEditProjectName(header, project);
    }
  });
  const deleteBtn = document.createElement('button');
  deleteBtn.className = 'project-group-btn';
  deleteBtn.title = '删除项目和会话';
  deleteBtn.type = 'button';
  deleteBtn.setAttribute('aria-label', '删除项目和会话');
  deleteBtn.textContent = '✕';
  deleteBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (isVirtualCwd) {
      if (confirm(`确定移除「${project.name}」分组？\n（会话将变为独立显示）`)) {
        dismissVirtualProject(project.path);
        renderSessionList();
      }
    } else {
      if (confirm(`确定删除项目「${project.name}」及其中 ${groupSessions.length} 个会话？\n此操作不会删除本地项目文件。`)) {
        deleteProjectWithSessions(project, groupSessions, 'header_button');
      }
    }
  });
  actions.appendChild(renameBtn);
  actions.appendChild(deleteBtn);

  const projectMoreBtn = document.createElement('button');
  projectMoreBtn.className = 'project-group-btn project-group-more-btn';
  projectMoreBtn.title = '更多操作';
  projectMoreBtn.type = 'button';
  projectMoreBtn.setAttribute('aria-label', '更多操作');
  projectMoreBtn.textContent = '⋯';
  projectMoreBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const selectedAgent = _getSelectedAgent?.() || 'claude';
    const doCreate = () => {
      const nextMode = _getSavedModeForAgent?.(selectedAgent) || 'yolo';
      send(isVirtualCwd
        ? { type: 'new_session', cwd: project.path, agent: selectedAgent, mode: nextMode }
        : { type: 'new_session', projectId: project.id, agent: selectedAgent, mode: nextMode });
    };
    const doRename = () => {
      if (isVirtualCwd) {
        undismissVirtualProject(project.path);
        _setPendingProjectSaveCallback?.((updatedProjects) => {
          const saved = updatedProjects.find((p) => p.path === project.path);
          if (saved) {
            const groupEl = findProjectGroupElement(saved.id);
            const headerEl = groupEl?.querySelector('.project-group-header');
            if (headerEl) startEditProjectName(headerEl as HTMLElement, saved);
          }
        });
        send({ type: 'save_project', path: project.path, name: project.name });
      } else {
        startEditProjectName(header, project);
      }
    };
    const doDelete = () => {
      if (isVirtualCwd) {
        if (confirm(`确定移除「${project.name}」分组？\n（会话将变为独立显示）`)) {
          dismissVirtualProject(project.path);
          renderSessionList();
        }
      } else {
        if (confirm(`确定删除项目「${project.name}」及其中 ${groupSessions.length} 个会话？\n此操作不会删除本地项目文件。`)) {
          deleteProjectWithSessions(project, groupSessions, 'more_menu');
        }
      }
    };
    const doMoveToFront = () => {
      if (!isVirtualCwd) send({ type: 'move_project_to_front', projectId: project.id });
    };
    const menuItems = [
      { label: '+ 新建会话', action: doCreate },
      ...(isVirtualCwd ? [] : [{ label: '⇧ 排在最前', action: doMoveToFront }]),
      { label: '✎ 重命名', action: doRename },
      { label: '✕ 删除项目和会话', danger: true, action: doDelete },
    ];
    showSidebarContextMenu(projectMoreBtn, menuItems);
  });
  actions.appendChild(projectMoreBtn);

  header.appendChild(main);
  if (actions.childElementCount) header.appendChild(actions);

  header.addEventListener('click', () => {
    if (isCollapsed) {
      collapsedProjects.delete(project.id);
    } else {
      collapsedProjects.add(project.id);
    }
    saveCollapsedProjects();
    renderSessionList();
  });
  group.appendChild(header);

  const body = document.createElement('div');
  body.className = 'project-group-body' + (isCollapsed ? ' collapsed' : '');
  if (!isCollapsed) {
    const sortedSessions = [...groupSessions].sort((a, b) => {
      const aOrder = projectSessionOrder.get(String(a?.id || '').trim()) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = projectSessionOrder.get(String(b?.id || '').trim()) ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
    for (const s of sortedSessions) {
      body.appendChild(buildSessionItem(s));
    }
    if (sortedSessions.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'project-group-empty';
      empty.textContent = '这个项目下还没有对话。';
      body.appendChild(empty);
    }
  }
  group.appendChild(body);
  container.appendChild(group);
}

function startEditProjectName(headerEl: HTMLElement, project: any): void {
  const nameEl = headerEl.querySelector('.project-group-name');
  const currentName = project.name || '';
  const input = document.createElement('input');
  input.className = 'session-item-edit-input';
  input.value = currentName;
  input.maxLength = 50;
  input.style.fontSize = '12px';
  input.style.fontWeight = '700';

  nameEl.replaceWith(input);
  input.focus();
  input.select();

  const actionsEl = headerEl.querySelector('.project-group-actions') as HTMLElement | null;
  if (actionsEl) actionsEl.style.display = 'none';

  function save() {
    const newName = input.value.trim();
    if (newName && newName !== currentName) {
      send({ type: 'rename_project', projectId: project.id, name: newName });
    }
    renderSessionList();
  }
  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
    if (e.key === 'Escape') { input.value = currentName; input.blur(); }
  });
}

export function renderSessionList(): void {
  const sessionList = getSessionList();
  if (!sessionList) return;
  const projects = _getProjects?.() || [];
  const savedScrollTop = sessionList.scrollTop;
  sessionList.innerHTML = '';
  const visibleSessions = _getVisibleSessions?.() || [];
  rememberProjectSessionOrder(visibleSessions);
  const listFragment = document.createDocumentFragment();
  const hasProjects = projects.length > 0;
  const sessionTimestampCache = new Map<string, number>();
  for (const session of visibleSessions) {
    sessionTimestampCache.set(session.id, getSessionUpdatedTimestamp(session));
  }

  if (visibleSessions.length === 0 && !hasProjects) {
    const empty = document.createElement('div');
    empty.className = 'session-list-empty';
    empty.textContent = '暂无会话，点击"新建/打开项目"开始。';
    listFragment.appendChild(empty);
    sessionList.appendChild(listFragment);
    _renderWorkspaceInsights?.();
    return;
  }

  if (visibleSessions.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'session-list-empty';
    empty.textContent = '当前还没有会话，你可以从下面任意项目继续新建。';
    listFragment.appendChild(empty);
  }

  const projectsById = new Map(projects.map((project) => [project.id, project]));
  const grouped = new Map<string, any[]>();
  const virtualProjectsById = new Map<string, any>();
  const ungrouped: any[] = [];

  for (const s of visibleSessions) {
    const matchedProject = findBestProjectForSession(s, projectsById);
    if (matchedProject) {
      if (!grouped.has(matchedProject.id)) grouped.set(matchedProject.id, []);
      grouped.get(matchedProject.id)!.push(s);
      continue;
    }
    const virtualProject = buildVirtualProjectFromSession(s);
    if (virtualProject) {
      const normalizedVirtualPath = normalizeDismissedVirtualProjectPath(virtualProject.path);
      const isDismissed = dismissedVirtualProjects.has(normalizedVirtualPath);
      logProjectRemoveDebug('virtual_project_candidate', {
        sessionId: s.id,
        sessionTitle: s.title || null,
        projectId: virtualProject.id,
        path: virtualProject.path,
        normalizedPath: normalizedVirtualPath,
        isDismissed,
        dismissed: [...dismissedVirtualProjects],
      });
      if (isDismissed) {
        ungrouped.push(s);
        continue;
      }
      if (!virtualProjectsById.has(virtualProject.id)) {
        virtualProjectsById.set(virtualProject.id, virtualProject);
      }
      if (!grouped.has(virtualProject.id)) grouped.set(virtualProject.id, []);
      grouped.get(virtualProject.id)!.push(s);
      continue;
    }
    ungrouped.push(s);
  }

  const groupEntries = projects.map((project) => ({
    project,
    groupSessions: grouped.get(project.id) || [],
    containsCurrentSession: (grouped.get(project.id) || []).some((session) => session.id === sessionState.currentSessionId),
    isVirtual: false,
    latestTimestamp: getLatestSessionTimestamp(grouped.get(project.id) || [], sessionTimestampCache),
  }));

  for (const project of virtualProjectsById.values()) {
    const groupSessions = grouped.get(project.id) || [];
    groupEntries.push({
      project,
      groupSessions,
      containsCurrentSession: groupSessions.some((session) => session.id === sessionState.currentSessionId),
      isVirtual: true,
      latestTimestamp: getLatestSessionTimestamp(groupSessions, sessionTimestampCache),
    });
  }

  groupEntries.sort((a, b) => {
    const priorityDiff = getProjectSortPriority(b.project) - getProjectSortPriority(a.project);
    if (priorityDiff !== 0) return priorityDiff;
    const aHasSessions = a.groupSessions.length > 0;
    const bHasSessions = b.groupSessions.length > 0;
    if (aHasSessions !== bHasSessions) {
      return aHasSessions ? -1 : 1;
    }
    if (a.isVirtual !== b.isVirtual) {
      return a.isVirtual ? 1 : -1;
    }
    return SESSION_LIST_COLLATOR.compare(a.project.name || '', b.project.name || '');
  });

  for (const entry of groupEntries) {
    renderProjectGroup(entry.project, entry.groupSessions, listFragment, sessionTimestampCache);
  }
  if (ungrouped.length > 0) {
    const sortedUngrouped = [...ungrouped].sort((a, b) => {
      const bTs = sessionTimestampCache.get(b.id) ?? getSessionUpdatedTimestamp(b);
      const aTs = sessionTimestampCache.get(a.id) ?? getSessionUpdatedTimestamp(a);
      return bTs - aTs;
    });
    for (const session of sortedUngrouped) {
      listFragment.appendChild(buildSessionItem(session));
    }
  }
  sessionList.appendChild(listFragment);
  _renderWorkspaceInsights?.();
  sessionList.scrollTop = savedScrollTop;
}

function removeSessionLocally(session: any): void {
  const sessionId = String(session?.id || '').trim();
  if (!sessionId) return;
  const sessionAgent = normalizeAgent(session?.agent);
  if (getLastSessionForAgent(sessionAgent) === sessionId) {
    localStorage.removeItem(getAgentSessionStorageKey(sessionAgent));
  }
  if (localStorage.getItem('webcoding-session') === sessionId) localStorage.removeItem('webcoding-session');
  invalidateSessionCache(sessionId);
}

function deleteProjectWithSessions(project: any, groupSessions: any[], source: string): void {
  const sessionsToDelete = Array.isArray(groupSessions) ? groupSessions.filter((session) => session?.id) : [];
  dismissVirtualProject(project.path);
  logProjectRemoveDebug('delete_project_with_sessions', {
    source,
    projectId: project.id,
    name: project.name,
    path: project.path,
    normalizedPath: normalizeDismissedVirtualProjectPath(project.path),
    sessionIds: sessionsToDelete.map((session) => session.id),
  });
  for (const session of sessionsToDelete) {
    removeSessionLocally(session);
    send({ type: 'delete_session', sessionId: session.id });
  }
  send({ type: 'delete_project', projectId: project.id });
  const selectedAgent = _getSelectedAgent?.() || 'claude';
  if (sessionsToDelete.some((session) => session.id === sessionState.currentSessionId)) {
    _resetChatView?.(selectedAgent);
  }
  renderSessionList();
}

function startEditSessionTitle(itemEl: HTMLElement, session: any): void {
  const titleEl = itemEl.querySelector('.session-item-title');
  const currentTitle = session.title || '';
  const input = document.createElement('input');
  input.className = 'session-item-edit-input';
  input.value = currentTitle;
  input.maxLength = 100;

  titleEl.replaceWith(input);
  input.focus();
  input.select();

  const rightEl = itemEl.querySelector('.session-item-right') as HTMLElement | null;
  if (rightEl) rightEl.style.display = 'none';

  function save() {
    const newTitle = input.value.trim() || currentTitle;
    if (newTitle !== currentTitle) {
      send({ type: 'rename_session', sessionId: session.id, title: newTitle });
    }
    const span = document.createElement('span');
    span.className = 'session-item-title';
    span.textContent = newTitle;
    input.replaceWith(span);
    if (rightEl) rightEl.style.display = '';
  }

  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
    if (e.key === 'Escape') { input.value = currentTitle; input.blur(); }
  });
}

export function highlightActiveSession(): void {
  document.querySelectorAll('.session-item').forEach((el) => {
    (el as HTMLElement).classList.toggle('active', (el as HTMLElement).dataset.id === sessionState.currentSessionId);
  });
}

// ============================================================
// Sidebar open/close
// ============================================================

export function openSidebar(): void {
  const sidebar = getSidebar();
  const sidebarOverlay = getSidebarOverlay();
  if (sidebar) sidebar.classList.add('open');
  if (sidebarOverlay) sidebarOverlay.hidden = false;
}

export function closeSidebar(): void {
  const sidebar = getSidebar();
  const sidebarOverlay = getSidebarOverlay();
  if (sidebar) sidebar.classList.remove('open');
  if (sidebarOverlay) sidebarOverlay.hidden = true;
}

// ============================================================
// Sidebar swipe handlers
// ============================================================

function canCloseSidebarBySwipe(target: EventTarget | null): boolean {
  const sidebar = getSidebar();
  const sidebarOverlay = getSidebarOverlay();
  if (!window.matchMedia('(max-width: 768px), (pointer: coarse)').matches) return false;
  if (!sidebar?.classList.contains('open')) return false;
  if (!target) return false;
  if ((target as Element).closest?.('#session-list')) return false;
  return (sidebar?.contains(target as Node) || target === sidebarOverlay) ?? false;
}

export function handleSidebarSwipeStart(e: TouchEvent): void {
  if (!e.touches || e.touches.length !== 1) return;
  const touch = e.touches[0];
  if (canCloseSidebarBySwipe(e.target)) {
    sidebarSwipe = {
      startX: touch.clientX,
      startY: touch.clientY,
      active: true,
      mode: 'close',
    };
    return;
  }
  // 移动端不再支持从内容区滑动展开侧边栏，避免误触；保留按钮打开和已打开后的滑动关闭。
  sidebarSwipe = null;
}

export function handleSidebarSwipeMove(e: TouchEvent): void {
  if (!sidebarSwipe?.active || !e.touches || e.touches.length !== 1) return;
  const touch = e.touches[0];
  const deltaX = touch.clientX - sidebarSwipe.startX;
  const deltaY = touch.clientY - sidebarSwipe.startY;
  if (Math.abs(deltaY) > SIDEBAR_SWIPE_MAX_VERTICAL_DRIFT && Math.abs(deltaY) > Math.abs(deltaX)) {
    sidebarSwipe = null;
    return;
  }
  const horizontalIntent = deltaX < -12;
  if (horizontalIntent && Math.abs(deltaY) < SIDEBAR_SWIPE_MAX_VERTICAL_DRIFT) {
    e.preventDefault();
  }
}

export function clearSidebarSwipe(): void {
  sidebarSwipe = null;
}

export function handleSidebarSwipeEnd(e: TouchEvent): void {
  if (!sidebarSwipe?.active) return;
  const touch = e.changedTouches && e.changedTouches[0];
  const endX = touch ? touch.clientX : sidebarSwipe.startX;
  const endY = touch ? touch.clientY : sidebarSwipe.startY;
  const deltaX = endX - sidebarSwipe.startX;
  const deltaY = endY - sidebarSwipe.startY;
  const shouldClose = sidebarSwipe.mode === 'close' &&
    deltaX <= -SIDEBAR_SWIPE_TRIGGER &&
    Math.abs(deltaY) <= SIDEBAR_SWIPE_MAX_VERTICAL_DRIFT;
  sidebarSwipe = null;
  if (shouldClose) {
    closeSidebar();
  }
}

// ============================================================
// Sidebar resize handlers
// ============================================================

export function handleSidebarResizeStart(e: PointerEvent): void {
  const sidebar = getSidebar();
  const sidebarResizer = getSidebarResizer();
  if (!canResizeSidebar()) return;
  if (typeof e.button === 'number' && e.button !== 0) return;
  sidebarResizeState = {
    startX: e.clientX,
    startWidth: sidebar!.getBoundingClientRect().width,
  };
  document.body.classList.add('sidebar-resizing');
  if (typeof sidebarResizer?.setPointerCapture === 'function' && e.pointerId !== undefined) {
    sidebarResizer.setPointerCapture(e.pointerId);
  }
  e.preventDefault();
}

export function handleSidebarResizeMove(e: PointerEvent): void {
  if (!sidebarResizeState) return;
  const deltaX = e.clientX - sidebarResizeState.startX;
  applySidebarWidth(sidebarResizeState.startWidth + deltaX, { skipPersist: true });
}

export function handleSidebarResizeEnd(e: PointerEvent): void {
  if (!sidebarResizeState) return;
  const sidebarResizer = getSidebarResizer();
  const releasedPointerId = e?.pointerId;
  if (typeof sidebarResizer?.releasePointerCapture === 'function' && releasedPointerId !== undefined) {
    try { sidebarResizer.releasePointerCapture(releasedPointerId); } catch {}
  }
  const currentWidth = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sidebar-width'), 10)
    || getSidebar()!.getBoundingClientRect().width
    || SIDEBAR_DEFAULT_WIDTH;
  applySidebarWidth(currentWidth);
  sidebarResizeState = null;
  document.body.classList.remove('sidebar-resizing');
}
